import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import styles from './PopupMenu.module.css';

type ComponentProps = {
  text: string;
  children: ReactNode;
};

// A tap focuses the button before its click toggles the menu, so focus from a pointer must not open it.
// Browsers without :focus-visible (Safari before 15.4) keep opening on any focus.
const isKeyboardFocus = (el: Element) => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

export const PopupMenu = ({ text, children }: ComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const pressedByMouse = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    // Hover leaves focus outside the menu, so the listener must sit on document from mount.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Focus moves first: onFocus would reopen the menu if the close came before it.
      if (menuRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
      setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className={styles.root}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') setIsOpen(true);
      }}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={(e) => {
        if (isKeyboardFocus(e.target)) setIsOpen(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsOpen(false);
        }
      }}
    >
      <button
        ref={triggerRef}
        aria-haspopup='true'
        aria-expanded={isOpen}
        aria-controls={menuId}
        className={styles.trigger}
        type='button'
        onPointerDown={(e) => {
          pressedByMouse.current = e.pointerType === 'mouse';
        }}
        onClick={() => {
          // Hover has already opened the menu for a mouse, so its click keeps it open. Keyboard and touch still toggle.
          if (pressedByMouse.current) setIsOpen(true);
          else setIsOpen((o) => !o);
          pressedByMouse.current = false;
        }}
      >
        {text}
      </button>
      <div
        ref={menuRef}
        id={menuId}
        className={styles.menu}
        hidden={!isOpen}
        onClick={(e) => {
          // The header outlives route changes, so choosing a link has to close the menu.
          if (e.target instanceof Element && e.target.closest('a')) setIsOpen(false);
        }}
      >
        {children}
      </div>
    </div>
  );
};
